/* A local product story: no microphone, cart or network APIs are used. */
(() => {
  if (customElements.get('mm-hero-story')) return;

  customElements.define('mm-hero-story', class extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.slides = [...this.querySelectorAll('[data-story-slide]')];
      if (!this.slides.length) return;
      this.photos = [...this.querySelectorAll('[data-story-photo]:not([data-story-slide])')];
      this.connected = true;
      this.currentIndex = Math.max(0, Math.min(Number(this.currentIndex) || 0, this.slides.length - 1));
      this.userPaused = Boolean(this.userPaused);
      this.hovered = this.matches(':hover');
      this.focused = this.contains(document.activeElement);
      this.inView = !('IntersectionObserver' in window);
      this.motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');

      this.onClick = event => {
        const button = event.target.closest('button');
        if (!button || button.disabled || !this.contains(button)) return;
        if (button.hasAttribute('data-story-select')) {
          const index = this.slides.findIndex(slide => slide.dataset.storyKey === button.dataset.storySelect);
          if (index !== -1) this.select(index, true);
        } else if (button.hasAttribute('data-story-prev')) this.select(this.currentIndex - 1, true);
        else if (button.hasAttribute('data-story-next')) this.select(this.currentIndex + 1, true);
        else if (button.hasAttribute('data-story-pause')) {
          this.userPaused = !this.userPaused;
          this.updatePause();
          this.restart();
        }
      };
      this.onEnter = () => { this.hovered = true; this.restart(); };
      this.onLeave = () => { this.hovered = false; this.restart(); };
      this.onFocusIn = () => { this.focused = true; this.restart(); };
      this.onFocusOut = event => {
        if (!this.contains(event.relatedTarget)) { this.focused = false; this.restart(); }
      };
      this.onVisibility = () => this.restart();
      this.onMotion = () => { this.updatePause(); this.restart(); };
      this.addEventListener('click', this.onClick);
      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibility);
      if (this.motion?.addEventListener) this.motion.addEventListener('change', this.onMotion);
      else this.motion?.addListener?.(this.onMotion);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          for (const entry of entries) if (entry.target === this) {
            this.inView = entry.isIntersecting && (entry.intersectionRatio ?? 1) >= 0.25;
            this.restart();
          }
        }, { threshold: [0, 0.25] });
        this.observer.observe(this);
      }

      this.querySelectorAll('[data-story-controls]').forEach(control => { control.hidden = this.slides.length < 2; });
      this.querySelectorAll('[data-story-select]').forEach(button => {
        button.disabled = !this.slides.some(slide => slide.dataset.storyKey === button.dataset.storySelect);
      });
      this.updatePause();
      this.select(this.currentIndex, false);
      this.dataset.storyReady = 'true';
    }

    automaticAllowed() {
      return this.slides.length > 1 && this.dataset.autoplay !== 'false' && this.dataset.designMode !== 'true' && !this.motion?.matches;
    }

    canRotate() {
      return this.connected && this.automaticAllowed() && !this.userPaused && !this.hovered && !this.focused && this.inView && !document.hidden;
    }

    updatePause() {
      const button = this.querySelector('[data-story-pause]');
      if (!button) return;
      button.hidden = !this.automaticAllowed();
      button.setAttribute('aria-pressed', String(this.userPaused));
      button.setAttribute('aria-label', `${this.userPaused ? 'Resume' : 'Pause'} automatic story rotation`);
      button.textContent = this.userPaused ? 'Resume' : 'Pause';
    }

    select(index, manual) {
      this.currentIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;
      const active = this.slides[this.currentIndex];
      this.slides.forEach(slide => {
        const inactive = slide !== active;
        slide.hidden = inactive;
        slide.toggleAttribute('inert', inactive);
        slide.setAttribute('aria-hidden', String(inactive));
      });
      this.photos.forEach(photo => { photo.hidden = photo.dataset.storyPhoto !== active.dataset.storyPhoto; });
      this.querySelectorAll('[data-story-select]').forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.storySelect === active.dataset.storyKey));
      });
      const frame = this.querySelector('[data-story-frame]');
      if (frame) {
        frame.dataset.device = active.dataset.storyDevice;
        frame.dataset.state = active.dataset.storyState;
      }
      const link = this.querySelector('[data-story-product]');
      if (link) link.href = active.dataset.storyUrl;
      const label = this.querySelector('[data-story-product-label]');
      if (label) label.textContent = active.dataset.storyLabel;
      const count = this.querySelector('[data-story-count]');
      if (count) count.textContent = `${String(this.currentIndex + 1).padStart(2, '0')} / ${String(this.slides.length).padStart(2, '0')}`;
      if (manual) {
        const announcement = this.querySelector('[data-story-announcement]');
        if (announcement) announcement.textContent = `${active.getAttribute('aria-label')}. Story ${this.currentIndex + 1} of ${this.slides.length}.`;
      }
      // Load a selected photo immediately; warming the next one must not compete
      // with the first hero image, font/style loading, or a constrained connection.
      const next = this.slides[(this.currentIndex + 1) % this.slides.length];
      this.photos.filter(photo => photo.dataset.storyPhoto === active.dataset.storyPhoto).forEach(photo => {
        photo.querySelectorAll('img').forEach(image => {
          image.setAttribute('loading', 'eager');
          if (image.getAttribute('fetchpriority') === 'low') image.setAttribute('fetchpriority', 'auto');
        });
      });
      this.prepareNextImage(active, next);
      this.dataset.activeStory = active.dataset.storyKey;
      this.restart();
    }

    prepareNextImage(active, next) {
      this.cancelImageWarmup();
      const connection = navigator.connection;
      if (!this.automaticAllowed() || connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return;
      const currentImage = this.photos.find(photo => photo.dataset.storyPhoto === active.dataset.storyPhoto)?.querySelector('img');
      this.nextImage = this.photos.find(photo => photo.dataset.storyPhoto === next.dataset.storyPhoto)?.querySelector('img[loading="lazy"]');
      if (!currentImage || !this.nextImage) return;
      const token = this.imageWarmToken;
      this.imageWarmCleanups = [];
      this.imageDecoded = false;
      this.pageLoaded = document.readyState === 'complete';
      const listen = (target, type, callback) => {
        target.addEventListener(type, callback, { once: true });
        this.imageWarmCleanups.push(() => target.removeEventListener(type, callback));
      };
      const ready = () => {
        if (!this.connected || token !== this.imageWarmToken) return;
        this.imageDecoded = true;
        this.requestImageWarmup();
      };
      if (!this.pageLoaded) listen(window, 'load', () => {
        if (token !== this.imageWarmToken) return;
        this.pageLoaded = true;
        this.requestImageWarmup();
      });
      if (typeof currentImage.decode === 'function') {
        try { currentImage.decode().then(ready, ready); } catch (_) { ready(); }
      } else if (currentImage.complete) ready();
      else { listen(currentImage, 'load', ready); listen(currentImage, 'error', ready); }
    }

    requestImageWarmup() {
      if (!this.nextImage || !this.imageDecoded || !this.pageLoaded || !this.canRotate() || this.imageWarmIdle != null || this.imageWarmTimer != null) return;
      const token = this.imageWarmToken;
      const warm = () => {
        if (!this.connected || token !== this.imageWarmToken) return;
        this.imageWarmIdle = null;
        this.imageWarmTimer = null;
        if (!this.canRotate()) return;
        this.nextImage.setAttribute('fetchpriority', 'low');
        this.nextImage.setAttribute('loading', 'eager');
        this.cancelImageWarmup();
      };
      if (typeof window.requestIdleCallback === 'function') this.imageWarmIdle = window.requestIdleCallback(warm, { timeout: 1500 });
      else this.imageWarmTimer = window.setTimeout(warm, 800);
    }

    cancelImageWarmup() {
      this.imageWarmToken = (this.imageWarmToken || 0) + 1;
      if (this.imageWarmIdle != null) window.cancelIdleCallback?.(this.imageWarmIdle);
      window.clearTimeout(this.imageWarmTimer);
      this.imageWarmIdle = null;
      this.imageWarmTimer = null;
      this.nextImage = null;
      this.imageDecoded = false;
      this.pageLoaded = false;
      this.imageWarmCleanups?.forEach(remove => remove());
      this.imageWarmCleanups = [];
    }

    restart() {
      clearTimeout(this.timer);
      this.timer = null;
      const playing = this.canRotate();
      this.dataset.playing = String(playing);
      this.requestImageWarmup();
      if (playing) this.timer = setTimeout(() => {
        this.timer = null;
        if (this.canRotate()) this.select(this.currentIndex + 1, false);
        else this.dataset.playing = 'false';
      }, 8000);
    }

    disconnectedCallback() {
      this.connected = false;
      clearTimeout(this.timer);
      this.timer = null;
      this.dataset.playing = 'false';
      this.observer?.disconnect();
      this.cancelImageWarmup();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('mouseenter', this.onEnter);
      this.removeEventListener('mouseleave', this.onLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.motion?.removeEventListener) this.motion.removeEventListener('change', this.onMotion);
      else this.motion?.removeListener?.(this.onMotion);
    }
  });
})();
